import { Link } from "react-router";
import { Alerta, Button, Card, Casilla, DataList, Dialog, MetricCard, MontoField, Row, Select, Spinner, Stack, TextField, type ColumnaDataList } from "@/components";
import { estadoSaldo, saldoContable, type EstadoSaldo } from "@/lib/cuentas";
import { aTextoApi, formatParticipacion } from "@/lib/participacion";
import { detalleSaldos, formatUsd, mensajeDiferencia, OPCIONES_PASO, porcentajeSobreTotal, type FilaPedido } from "@/lib/pedidos";
import { usePedidoTab } from "./usePedidoTab";

const COLOR_SALDO: Record<EstadoSaldo, string> = { a_favor: "text-exito", debe: "text-peligro", al_dia: "text-texto-suave" };

export function PedidoTab() {
  const t = usePedidoTab();
  const pedido = t.pedido;

  const columnas: ColumnaDataList<FilaPedido>[] = [
    { clave: "nombre", titulo: "Fiduciante", render: (f) => f.nombre },
    { clave: "porcentaje", titulo: "Participación", render: (f) => formatParticipacion(aTextoApi(f.porcentaje)) },
    { clave: "justo", titulo: "Parte justa", render: (f) => formatUsd(f.justo) },
    ...(pedido?.conSaldos
      ? [{ clave: "saldo", titulo: "Saldo", render: (f: FilaPedido) => <span className={COLOR_SALDO[estadoSaldo(f.saldo ?? "0.00")]}>{saldoContable(f.saldo ?? "0.00")}</span> }]
      : []),
    { clave: "pedido", titulo: "A pedir", render: (f) => <span className="font-semibold">{formatUsd(f.pedido)}</span> },
    {
      clave: "sobre",
      titulo: "Diferencia",
      render: (f) => (
        <span className="text-texto-suave">
          {mensajeDiferencia(f.sobre, "")} · {pedido ? porcentajeSobreTotal(f, pedido.total) : "—"} del total
        </span>
      ),
    },
  ];

  const acciones = (f: FilaPedido) => (
    <Row espacio={2}>
      <Button variante="secundario" tamanio="sm" aria-label={`Bajar a ${f.nombre}`} disabled={!f.puedeBajar} onClick={() => t.ajustar(f.fiduciante_id, -1)}>
        −
      </Button>
      <Button variante="secundario" tamanio="sm" aria-label={`Subir a ${f.nombre}`} onClick={() => t.ajustar(f.fiduciante_id, 1)}>
        +
      </Button>
    </Row>
  );

  return (
    <Stack espacio={6}>
      <Stack espacio={2}>
        <h1 className="text-pantalla font-light tracking-[-0.03em]">Armar previsión</h1>
        <p className="text-chico text-texto-suave">
          Escribí cuánto necesitás juntar y mirá cuánto le toca a cada uno por su participación, redondeado para arriba. Con los saldos, a cada uno se le suma lo
          que debe y se le descuenta lo que tiene a favor. Con − y + subís o bajás a cada uno de a un paso. Cuando te cierre, emití la previsión con esos montos; cuando te pasen la plata, cargá el aporte.
        </p>
      </Stack>

      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.errorCarga && (
        <Stack espacio={3}>
          <Alerta texto={t.errorCarga} />
          <div>
            <Button variante="secundario" onClick={t.reintentar}>
              Reintentar
            </Button>
          </div>
        </Stack>
      )}
      {t.sinDistribucion && (
        <Alerta
          tono="alerta"
          texto="Hoy no rige ninguna distribución completa: sin porcentajes no hay cómo repartir."
          accion={<Link to="/participacion" className="font-medium underline underline-offset-2">Cargala en Participación</Link>}
        />
      )}

      {!t.cargando && !t.errorCarga && !t.sinDistribucion && (
        <>
          <Card>
            <div className="grid gap-4 sm:grid-cols-2">
              <MontoField etiqueta="Monto a juntar" valor={t.monto} onCambio={t.cambiarMonto} moneda="USD" ayuda="Lo que necesitás, antes de redondear." />
              <Select etiqueta="Redondear para arriba a" opciones={OPCIONES_PASO} valor={t.paso} onCambio={t.cambiarPaso} />
              <Casilla
                className="sm:col-span-2"
                etiqueta="Tener en cuenta los saldos"
                marcado={t.conSaldos}
                onCambio={t.cambiarConSaldos}
                ayuda="De Cuentas corrientes: lo que debe se suma y lo que tiene a favor se descuenta (si pasa su parte, se le pide 0 y el resto le sigue quedando a favor)."
              />
            </div>
          </Card>

          {pedido && (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                <MetricCard titulo="Necesitás" valor={formatUsd(pedido.objetivo)} detalle={detalleSaldos(pedido) ?? undefined} />
                <MetricCard titulo="Pedís en total" valor={formatUsd(pedido.total)} />
                <MetricCard
                  titulo="Diferencia"
                  valor={mensajeDiferencia(pedido.diferencia, "")}
                  detalle={pedido.conSaldos ? "Entre lo que pedís y lo que hay que cubrir." : "Entre lo que pedís y lo que necesitás."}
                />
              </div>

              <DataList etiqueta="Pedido por fiduciante" columnas={columnas} filas={pedido.filas} claveFila={(f) => f.fiduciante_id} acciones={acciones} />

              <Row envolver espacio={3}>
                <Button onClick={t.abrirLlamado} disabled={!t.puedeArmarLlamado}>
                  Emitir la previsión
                </Button>
                <Button variante="secundario" onClick={t.descargar}>
                  Descargar (Excel)
                </Button>
                <Button variante="secundario" onClick={t.copiar}>
                  {t.copiado ? "Copiado" : "Copiar la lista"}
                </Button>
                <Button variante="secundario" onClick={t.subirTodos}>
                  Subir a todos un paso
                </Button>
                {t.hayAjustes && (
                  <Button variante="fantasma" onClick={t.volverAlRedondeo}>
                    Volver al redondeo
                  </Button>
                )}
              </Row>
            </>
          )}
        </>
      )}

      <Dialog abierto={t.dialogoLlamado} onCambio={(abierto) => !abierto && t.cerrarLlamado()} titulo="Emitir la previsión">
        <form onSubmit={t.emitir} noValidate>
          <Stack espacio={4}>
            <p className="text-chico">
              Se emite una previsión por <span className="font-semibold">{pedido ? formatUsd(pedido.total) : "—"}</span> con lo que le pedís a cada uno, no con el
              reparto exacto por %. Después no se pueden cambiar los montos: si hace falta, se borra y se arma de nuevo.
            </p>
            <Row espacio={3} envolver>
              <TextField etiqueta="Fecha" type="date" value={t.form.fecha} onChange={(e) => t.cambiar("fecha", e.target.value)} error={t.errores.fecha} />
              <TextField etiqueta="Vencimiento" type="date" value={t.form.vencimiento} onChange={(e) => t.cambiar("vencimiento", e.target.value)} error={t.errores.vencimiento} />
            </Row>
            <TextField etiqueta="Descripción" ayuda="Opcional, ej.: hormigón de la losa 3" value={t.form.descripcion} onChange={(e) => t.cambiar("descripcion", e.target.value)} error={t.errores.descripcion} />
            <Alerta texto={t.errorEmitir} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrarLlamado}>
                Cancelar
              </Button>
              <Button type="submit" cargando={t.emitiendo}>
                Emitir previsión
              </Button>
            </Row>
          </Stack>
        </form>
      </Dialog>
    </Stack>
  );
}
